import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  CircularProgress,
  DialogActions,
  DialogContent,
  makeStyles,
} from "@material-ui/core";
import {
  CorpModalScreens,
  IPerson,
  MODAL_ALERT,
  MODAL_ALERT_CHOICE,
  ModalAlertProperties,
  ModalButtonType,
  ModalCategoryType,
} from "redmond";
import {
  personToTravelerProfile,
  travelerProfileToPerson,
  TravelerProfile,
  TripsTravelerInfoForm,
  CorporateButton,
} from "halifax";
import { ArrowBack } from "@material-ui/icons";
import { CorporateEmbeddedProvider, EmbeddedTravelerForm } from "@capone-corporate/portal";
import { useExperimentIsVariant } from "@capone/experiments";
import { StepProps } from "../types";

import baseClasses from "../../styles.module.css";
import Header from "../Header";
import { trackEvent } from "../../../../../api/v1/trackEvent";

interface IProps extends Omit<StepProps, "primaryAction"> {
  primaryAction: (traveler: IPerson) => Promise<void>;
  traveler?: IPerson;
  isMobile?: boolean;
}

const useStyles = makeStyles(() => ({
  leftButtonWrapper: {
    "& .MuiButton-label": {
      justifyContent: "normal",
    },
  },
  mobileButton: {
    "& .MuiButton-label": {
      justifyContent: "center",
    },
  },
}));

const CreateTraveler = ({
  primaryAction,
  secondaryAction,
  onClose,
  traveler,
  isMobile = false,
}: IProps): React.ReactElement => {
  const useEmbeddedTravelerForm = useExperimentIsVariant(
    "corp-embedded-traveler-form",
    "available"
  );

  const [isDisabled, setIsDisabled] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const resetSavingState = () => {
    setIsSaving(false);
  };

  const classes = useStyles();

  const isMissingInfo = Boolean(
    traveler && "isMissingInfo" in traveler && traveler.isMissingInfo
  );
  const createProfileMode = isMissingInfo || !traveler;
  const titleTextPrefix = createProfileMode ? `Create` : `Confirm`;
  const modalTitle = `${titleTextPrefix} your traveler profile`;
  const subtitleCreateConfirm = createProfileMode ? `create` : `confirm`;
  const modalSubtitle = `First things first, let’s ${subtitleCreateConfirm} your traveler profile. Please make sure all the information added matches your government issued ID as this will be applied to travel bookings moving forward.`;

  const embeddedTraveler = useMemo(
    () => personToTravelerProfile(traveler),
    [traveler]
  );

  const commonTrackingProperties = useMemo(
    () =>
    ({
      type: createProfileMode
        ? "create_traveler_profile_onboarding"
        : "confirm_traveler_profile_onboarding",
      screen: CorpModalScreens.ONBOARDING_DETAILS,
      primary_button: "save and continue",
      secondary_button: "back",
      step: CorpModalScreens.ONBOARDING_DETAILS,
      funnel: "other",
      category: ModalCategoryType.FEATURE,
      modal_subtitle: modalSubtitle,
      modal_title: modalTitle,
      agent_title: modalTitle,
      agent_subtitle: modalSubtitle,
    } satisfies ModalAlertProperties),
    []
  );

  useEffect(() => {
    trackEvent({
      eventName: MODAL_ALERT,
      properties: commonTrackingProperties,
    });
  }, []);

  const handleSaveClick = () => {
    trackEvent({
      eventName: MODAL_ALERT_CHOICE,
      properties: {
        ...commonTrackingProperties,
        button_choice: ModalButtonType.PRIMARY,
      },
    });
    setIsSaving(true);
  };

  const back = () => {
    trackEvent({
      eventName: MODAL_ALERT_CHOICE,
      properties: {
        ...commonTrackingProperties,
        button_choice: ModalButtonType.SECONDARY,
      },
    });
    secondaryAction();
  };

  const renderEmbeddedForm = () => (
    <CorporateEmbeddedProvider>
      <EmbeddedTravelerForm
        mode={traveler ? "edit" : "add"}
        traveler={embeddedTraveler}
        showDriverLicense
        showContactEmail
        isContactEmailOptional
        cancelButtonText="Back"
        actionsConfig={{ layout: "split", sticky: true }}
        onSave={(profile: TravelerProfile) => {
          const updatedTraveler = travelerProfileToPerson(
            profile,
            traveler
          ) as IPerson;
          trackEvent({
            eventName: MODAL_ALERT_CHOICE,
            properties: {
              ...commonTrackingProperties,
              button_choice: ModalButtonType.PRIMARY,
            },
          });
          return primaryAction(updatedTraveler);
        }}
        onCancel={back}
      />
    </CorporateEmbeddedProvider>
  );

  const renderLegacyFormContent = () => (
    <TripsTravelerInfoForm
      traveler={traveler}
      handleUpdatePassenger={(updatedTraveler) => {
        primaryAction(updatedTraveler)
          .then(() => {
            resetSavingState();
          })
          .catch(() => {
            resetSavingState();
          });
      }}
      travelerInfoTitle="Traveler information"
      driverLicenseSectionSubtitle="This information will be required in order for you to book rental cars."
      buttonText="Save and continue"
      showGenderField
      showNationalityField
      showDriverLicenseSection
      disableDriversLicenseValidation
      showSecondaryButton
      secondaryButtonText="Back"
      onSecondaryButtonClick={secondaryAction}
      triggerSave={isSaving}
      setPrimaryButtonDisabled={setIsDisabled}
      hideButtons
      showPassportSection
      className={baseClasses.onboardingModalForm}
      isOnboarding
      showContactEmailSection
      tenant="capone-corporate"
      isMobile={isMobile}
    />
  );

  const renderLegacyFormActions = () => (
      <DialogActions
        className={
          isMobile ? "mobile-dialog-actions" : "create-traveler-actions"
        }
        style={{
          display: "flex",
          flexDirection: isMobile ? "column-reverse" : "row",
          justifyContent: !isMobile ? "space-between" : "flex-end",
          width: "auto",
          alignItems: "center",
          boxShadow: "0px -2px 4px rgba(0, 0, 0, 0.15)",
          boxSizing: isMobile ? "border-box" : "content-box",
        }}
      >
        <CorporateButton
          className={
            isMobile ? classes.mobileButton : classes.leftButtonWrapper
          }
          onClick={back}
          size={isMobile ? "large" : "medium"}
          variant="text"
          icon={<ArrowBack fontSize="small" />}
          data-testid="create-traveler-profile-secondary-cta"
          fullWidth={isMobile}
          style={
            isMobile
              ? {
                textAlign: "center",
                display: "flex",
                justifyContent: "center",
              }
              : {}
          }
        >
          Back
        </CorporateButton>
        <CorporateButton
          className={isMobile ? classes.mobileButton : ""}
          size={isMobile ? "large" : "medium"}
          onClick={handleSaveClick}
          disabled={isDisabled}
          data-testid="create-traveler-profile-primary-cta"
          fullWidth={isMobile}
        >
          {isSaving ? (
            <CircularProgress size={24} style={{ color: "white" }} />
          ) : (
            `Save and continue`
          )}
        </CorporateButton>
      </DialogActions>
  );

  return (
    <Box className={baseClasses.stepWrap}>
      <Header
        title={modalTitle}
        subtitle={modalSubtitle}
        currentStep={0}
        onClose={onClose}
        isMobile={isMobile}
      />
      <DialogContent
        id="onboarding-description"
        style={{
          flex: "1 1 auto",
          overflow: useEmbeddedTravelerForm ? "hidden" : "auto",
          minHeight: 0,
          display: useEmbeddedTravelerForm ? "flex" : undefined,
          flexDirection: useEmbeddedTravelerForm ? "column" : undefined,
          isolation: "isolate",
          contain: "paint",
          ...(isMobile ? { paddingLeft: 0, paddingRight: 0 } : {}),
        }}
      >
        <Box
          style={{
            ...(isMobile ? { paddingLeft: 16, paddingRight: 16 } : undefined),
            ...(useEmbeddedTravelerForm
              ? {
                  flex: "1 1 auto",
                  minHeight: 0,
                  display: "flex",
                  flexDirection: "column",
                  width: "100%",
                }
              : undefined),
          }}
        >
          {useEmbeddedTravelerForm
            ? renderEmbeddedForm()
            : renderLegacyFormContent()}
        </Box>
      </DialogContent>
      {!useEmbeddedTravelerForm && renderLegacyFormActions()}
    </Box>
  );
};

export default CreateTraveler;
